import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import type { PaymentFormValues } from 'Src/types/payment';
import moment from 'moment';
import * as yup from 'yup';
import { t } from 'Src/i18n';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { Title } from 'Components/frameworks/title/Title';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { PaymentFormikInput } from 'Components/widgets/payment-info/paymentFormik';
import { castAs } from 'Src/types/cast-helpers';
export type FreeDepositProps = {
  paymentSchedule: PaymentSchedule;
  deadlineDate: number;
  price: Price;
  totalPrice: Price | null | undefined;
  touched: boolean;
};
export const FreeDeposit = ({
  price,
  totalPrice,
  touched,
  paymentSchedule,
  deadlineDate,
}: FreeDepositProps) => {
  const validateFreeDeposit = yup
    .number()
    .typeError(t('booking.banking.splitPayment.paymentField'))
    .positive(t('booking.banking.splitPayment.amountError'))
    .max(price.value, t('booking.banking.splitPayment.amountError'));
  const { repaymentSchedules, depositAmount } = paymentSchedule;
  const hasPaymentPlan = repaymentSchedules.length;
  const formik = useFormikContext<PaymentFormValues>();
  const paymentsBefore = hasPaymentPlan
    ? [
        {
          deadline: undefined,
          amount: depositAmount.formattedValue,
        },
        {
          deadline: repaymentSchedules[0].deadline,
          amount: repaymentSchedules[0].amount.formattedValue,
        },
      ]
    : [
        {
          deadline: deadlineDate,
          amount: price.formattedValue,
        },
      ];
  useEffect(() => {
    if (hasPaymentPlan) {
      formik.setFieldValue('freeDeposit', depositAmount.value);
    }
  }, [hasPaymentPlan]);
  return (
    <div data-testid="Free-AmountBlock">
      <Title className="mb-10" theme="title-brand-n2">
        {t('stays.linkLabels.splitPaymentLabel')}
      </Title>
      <Capsule header="none" contentClassName="p-20">
        <div className="mb-10 flex flex-col gap-5 text-14 md:flex-row md:items-baseline">
          <p className="text-18 font-bold text-black">
            {t('booking.banking.splitPayment.totalDue')}
          </p>
          <ul className="flex-1">
            {paymentsBefore.map(({ deadline, amount }) => (
              <li className="flex items-center justify-between gap-5 text-orange" key={amount}>
                {t('booking.banking.splitPayment.paymentBefore')}{' '}
                {moment(deadline).format(t('booking.form.dateFormat'))}{' '}
                <span className="whitespace-no-wrap text-20 font-bold">{amount}</span>
              </li>
            ))}
            {totalPrice?.formattedValue && paymentsBefore.length === 1 && (
              <li className="flex justify-end text-20">
                <TemplateString
                  template={t('booking.banking.totalAmount')}
                  values={{
                    amount: totalPrice.formattedValue,
                  }}
                />
              </li>
            )}
          </ul>
        </div>

        <PaymentFormikInput
          label={t('booking.banking.splitPayment.title')}
          name="freeDeposit"
          onWheel={() => {
            castAs<HTMLElement | null>(document?.activeElement)?.blur();
          }}
          placeholder={!touched ? t('booking.banking.splitPayment.paymentField') : ''}
          title={t('booking.banking.splitPayment.paymentField')}
          type="number"
          validate={validateFreeDeposit}
        />
      </Capsule>
    </div>
  );
};
